import { useEffect } from 'react';
import { DownArrow } from '@/components/icons/down-arrow';
import { UpArrow } from '@/components/icons/up-arrow';
import useAnalyticsCalls from '@/hooks/use-analytics';
import { RiskImpactModalProps } from './risk-impact-modal.types';

export function RiskImpactModal({ isOpen, onClose, variant, impacts }: RiskImpactModalProps) {
  const { callModalViewed } = useAnalyticsCalls();

  useEffect(() => {
    if (isOpen) {
      callModalViewed(' Action Completion Risk Impact', {
        variant,
        impact_count: impacts.length.toString(),
      });
    }
  }, [isOpen, variant, impacts.length, callModalViewed]);

  if (!isOpen) return null;

  const hasImpacts = impacts.length > 0;
  const isPositive = variant === 'positive';
  const isNeutral = variant === 'neutral';

  return (
    <div className="dashboard-reset max-h-[600px]">
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
        onClick={onClose}
      >
        {/* Modal Content */}
        <div
          className="relative flex max-h-[600px] w-full max-w-[440px] rounded-[10px] bg-white shadow-[0px_10px_15px_-3px_rgba(0,0,0,0.1),0px_4px_6px_-2px_rgba(0,0,0,0.05)]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex min-h-0 w-full flex-col">
            {/* Header - Fixed */}
            <div className="flex-shrink-0 p-5 pb-0 sm:p-6">
              <div className="flex w-full flex-col items-start rounded-lg bg-white">
                <div className="flex w-full flex-col justify-center text-center text-lg leading-[28px] tracking-[0.208px] text-[#183344] sm:text-xl">
                  <p className="leading-7">
                    {!hasImpacts && (
                      <>
                        <span>Your estimated cancer risk </span>
                        <span className="font-bold">didn't change</span>
                        <span> based on your latest updates.</span>
                      </>
                    )}
                    {hasImpacts && isPositive && (
                      <>
                        <span>Good news - your estimated cancer risk has </span>
                        <span className="font-bold">gone down.</span>
                      </>
                    )}
                    {hasImpacts && isNeutral && (
                      <>
                        <span>Your estimated cancer risk has </span>
                        <span className="font-bold">changed</span>
                        <span> based on your latest updates.</span>
                      </>
                    )}
                    {hasImpacts && !isPositive && !isNeutral && (
                      <>
                        <span>Your estimated cancer risk has </span>
                        <span className="font-bold">gone up.</span>
                      </>
                    )}
                  </p>
                </div>
              </div>
            </div>

            {/* Impact List - Scrollable - only show if there are impacts */}
            {hasImpacts && (
              <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6 sm:py-6">
                <div className="flex w-full items-center gap-2.5 rounded-lg border border-solid border-[#d4c2b5] bg-white p-4 sm:p-6">
                  <div className="flex w-full flex-col items-start gap-3 sm:gap-4">
                    {impacts.map((impact, index) => {
                      // Determine direction for this specific impact
                      // Negative change = risk decreased (positive outcome)
                      // Positive change = risk increased (negative outcome)
                      const isImpactPositive = impact.change < 0;
                      const impactActionText = isImpactPositive ? 'reduced' : 'increased';
                      const ImpactArrowIcon = isImpactPositive ? DownArrow : UpArrow;

                      return (
                        <div key={`${impact.diseaseSlug}-${index}`} className="flex w-full items-center gap-3 sm:gap-4">
                          <div
                            className={`flex h-6 shrink-0 items-center justify-center gap-1 rounded-[2px] px-2 ${
                              isImpactPositive ? 'bg-[#7cf1c7]' : 'bg-[#ff9473]'
                            }`}
                            style={{ minWidth: '54px' }}
                          >
                            <ImpactArrowIcon width={12} height={12} />
                            <span
                              className={`text-xs leading-[18px] font-bold tracking-[0.208px] uppercase ${
                                isImpactPositive ? 'text-[#136d38]' : 'text-[#183344]'
                              }`}
                            >
                              {Math.abs(impact.change).toFixed(1)}%
                            </span>
                          </div>
                          <div className="flex flex-col justify-center text-sm leading-5 tracking-[0.208px] text-[#183344] sm:text-base sm:leading-6">
                            <p className="mb-0">
                              You {impactActionText} your {impact.disease.toLowerCase()} cancer risk
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* Spacing for when there are no impacts */}
            {!hasImpacts && <div className="py-5 sm:py-6" />}

            {/* Close Button - Fixed */}
            <div className="flex-shrink-0 p-5 pt-0 sm:p-6">
              <button
                className="flex h-10 w-full items-center justify-center gap-2.5 rounded-[100px] bg-[#183344] px-5 py-2.5 transition-colors hover:bg-[#2a4859] sm:h-12"
                onClick={onClose}
              >
                <p className="text-sm leading-[1.4] font-bold text-nowrap whitespace-pre text-white">Close</p>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
